import { ModalWindow } from '@cian/ui-kit';
import { useSelector } from 'react-redux';

import { trackMetroModalOpenEvent } from './tracking';

import { UndergroundMapLineSelector } from './UndergroundMapLineSelector';

import type { IUndergroundMapApi } from '@cian/underground-map-widget';
import { EMetroTravelType } from '../../../../../../packages/api-models/common/json_query';
import { LoadableErrorBoundary } from '../../../../../../packages/LoadableHelpers';
import { fetchUndergroundMapData } from 'shared/services/fetchUndergroundMapData';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { SearchButtonContainer } from '../../SearchButtonContainer';
import { useAppDispatch, useJsonQueryBackup } from 'shared/hooks';
import { UndergroundMapContainerLoadable } from './UndergroundMapContainer';
import { useRef, useCallback, FC, RefObject } from 'react';
import {
  selectJsonQuery,
  selectTravelTime,
  selectTravelType,
  selectUndergroundStations,
} from 'shared/selectors/jsonQuery';
import { selectLocationIdForMetroMap } from 'shared/selectors/geo';
import { setJsonQuery } from 'shared/slices/jsonQuery';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { IStationData } from 'shared/types/geo';
import { setUndergroundTravel } from '../../../../../../packages/JsonQuery/setUndergroundTravel';
import { unsetUndergroundTravel } from '../../../../../../packages/JsonQuery/unsetUndergroundTravel';
import { UndergroundMapModalFooter, UndergroundMapModalHeader } from '../../../../../../packages/GeoComponents';
import { updateTags } from 'shared/thunks/tags';
import { applyFilters } from 'shared/thunks/filters';
import { removeUndergroundStations, appendUndergroundStations } from 'shared/thunks/geo/undergrounds';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { getDefaultTravelTimeForType } from 'shared/utils/underground';
import { updateLocalStorageFiltersWereApplied } from 'shared/utils/uxfbLocalStorage/uxfbLocalStorage';

interface IUndergroundMapModalContainerProps {
  onModalClose: VoidFunction;
}

export const UndergroundMapModalContainer: FC<IUndergroundMapModalContainerProps> = ({ onModalClose }) => {
  const ctx = useApplicationContext();
  const dispatch = useAppDispatch();
  const undergroundMapApiRef = useRef<IUndergroundMapApi>(null);
  const selectedStations = useSelector(selectUndergroundStations);
  const locationId = useSelector(selectLocationIdForMetroMap);
  const travelTime = useSelector(selectTravelTime);
  const travelType = useSelector(selectTravelType) || EMetroTravelType.Any;
  const jsonQuery = useSelector(selectJsonQuery);
  const { resetJsonQuery } = useJsonQueryBackup();

  const handleRemove = useCallback(
    (stations: IStationData[]) => {
      dispatch(removeUndergroundStations(stations));
    },
    [dispatch],
  );

  const handleSelect = useCallback(
    (stations: IStationData[]) => {
      dispatch(appendUndergroundStations(stations));
    },
    [dispatch],
  );

  const handleApply = useCallback(async () => {
    onModalClose();
    dispatch(applyFilters());
    dispatch(updateTags(true));
    updateLocalStorageFiltersWereApplied();
  }, [dispatch, onModalClose, updateLocalStorageFiltersWereApplied]);

  const handleClose = useCallback(() => {
    onModalClose();
    resetJsonQuery();
  }, [onModalClose, resetJsonQuery]);

  const handleTravelInfoChange = (newTravelType: EMetroTravelType, newTravelTime?: number) => {
    let newJsonQuery;
    if (travelType !== EMetroTravelType.Any && newTravelType === EMetroTravelType.Any) {
      // Переключили на тип "неважно". Сбрасываем все фильтры
      newJsonQuery = unsetUndergroundTravel(jsonQuery)();
    } else if (travelType === EMetroTravelType.Any && newTravelType !== EMetroTravelType.Any) {
      // Переключили на тип, отличный от "неважно". Устанавливаем фильтр времени в дефолт
      newJsonQuery = setUndergroundTravel(jsonQuery)(newTravelType, getDefaultTravelTimeForType(newTravelType));
    } else {
      // В остальных случаях применяем выбранные фильтры, как есть.
      // Если время не указано, то его не будет в запросе и фильтр будет работать аналогично типу "неважно"
      newJsonQuery = setUndergroundTravel(jsonQuery)(newTravelType, newTravelTime);
    }

    dispatch(setJsonQuery(newJsonQuery));
    dispatch(updateSearchMeta());
  };

  const handleFetchData = useCallback((id: number) => fetchUndergroundMapData(id, ctx), [ctx]);

  /* istanbul ignore next */
  const handleRetry = () => UndergroundMapContainerLoadable.load();

  return (
    <ActionAfterViewObserver callback={trackMetroModalOpenEvent} triggerOnce>
      <ModalWindow
        fixed
        size="M"
        escape
        width="100%"
        maxWidth={1300}
        open
        onClose={handleClose}
        header={
          <UndergroundMapModalHeader
            fastSelectionButtons={
              <UndergroundMapLineSelector api={undergroundMapApiRef as RefObject<IUndergroundMapApi>} />
            }
            travelType={travelType}
            travelTime={travelTime}
            onUndergroundTravelChanged={handleTravelInfoChange}
          />
        }
        footer={
          <UndergroundMapModalFooter>
            <SearchButtonContainer onClick={handleApply} />
          </UndergroundMapModalFooter>
        }
      >
        <LoadableErrorBoundary logger={ctx.logger} onRetry={handleRetry}>
          <UndergroundMapContainerLoadable
            apiRef={undergroundMapApiRef as RefObject<IUndergroundMapApi>}
            locationId={locationId}
            onDataRequested={handleFetchData}
            onRemove={handleRemove}
            onSelect={handleSelect}
            selectedStations={selectedStations}
          />
        </LoadableErrorBoundary>
      </ModalWindow>
    </ActionAfterViewObserver>
  );
};
